import * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChatMessage } from "../../types";
import { join } from "../../utils/classNames";
import { AgentTrace } from "./components/AgentTrace";
import styles from "./Message.module.css";

interface Props {
	message: ChatMessage;
	assistantIconUrl?: string;
}

const markdownComponents = {
	a: ({ href, children, ...props }: React.ComponentPropsWithoutRef<"a">) => (
		<a {...props} href={href} target={'_blank'} rel={'noopener noreferrer'}>
			{children}
		</a>
	),
};

const TypingDots = () => (
	<div className={styles.typing}>
		<span />
		<span />
		<span />
	</div>
);

// Memoised: a streaming reply re-renders the list every frame, and unchanged messages keep
// their object identity, so this skips re-parsing their markdown.
export const Message = React.memo<Props>(({ message, assistantIconUrl }) => {
	const isUser = message.role === "user";
	// A streamed reply that fails keeps its partial answer in normal styling, with a note under it.
	const isStreamed = message.status !== undefined;
	const isError = !!message.error && !isStreamed;
	const interrupted = !!message.error && isStreamed;
	const steps = message.steps ?? [];
	const awaitingFirstEvent = message.status === "streaming" && !message.content && steps.length === 0;

	return (
		<div className={join(styles.row, isUser ? styles.rowUser : styles.rowAssistant, isError && styles.rowError)}>
			{!isUser && assistantIconUrl && (
				<div className={styles.avatar}>
					<img src={assistantIconUrl} alt="" width={28} height={28} loading="lazy" />
				</div>
			)}
			<div className={join(styles.body, steps.length > 0 && styles.bodyWide)}>
				{isUser ? (
					message.content
				) : (
					<>
						{steps.length > 0 && <AgentTrace steps={steps} />}
						{awaitingFirstEvent && <TypingDots />}
						{message.content && (
							<div className={styles.markdown}>
								<ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{message.content}</ReactMarkdown>
							</div>
						)}
						{interrupted && <div className={styles.interrupted}>{"Response interrupted — please try again."}</div>}
					</>
				)}
			</div>
		</div>
	);
});

export const TypingMessage: React.FC<{ assistantIconUrl?: string }> = ({ assistantIconUrl }) => (
	<div className={join(styles.row, styles.rowAssistant)}>
		{assistantIconUrl && (
			<div className={styles.avatar}>
				<img src={assistantIconUrl} alt="" width={28} height={28} loading="lazy" />
			</div>
		)}
		<div className={styles.body}>
			<TypingDots />
		</div>
	</div>
);
